import type { Download } from "./client";
import type { Helpers } from "./endpoints-shared";
import type { OperationId, Out } from "./operations";
import type { PageRequest } from "./types";

/** Whose documents to list: one owner (a member, household, vendor, bill, expense, Jonas transaction or statement), or everything filed under a household. */
export type DocumentOwnerFilter =
  | { ownerType: "member" | "household" | "vendor" | "bill" | "expense" | "transaction" | "statement"; ownerId: string }
  | { householdId: string };

/**
 * The documents module (ADR 0007): the documents of an owner and their files. Every method names its operation id, so a renamed route
 * fails to compile after `npm run api:generate`.
 */
export function documentsEndpoints({ http, get, post, paged }: Helpers) {
  /** A file download. The operation id is checked against the schema, so a renamed route fails to compile. */
  const file = (_operation: OperationId, path: string, signal?: AbortSignal) => http.request<Download>("GET", path, { responseType: "blob", signal });
  return {
    library: (page: PageRequest & { q?: string } = {}) => get<Out<"documents_list_library">>("/documents/library", paged(page, { q: page.q })),
    fileVersions: (id: string, signal?: AbortSignal) => get<Out<"documents_file_versions">>(`/documents/library/${id}/versions`, { signal }),
    uploadFileVersion: (id: string, version: number, title: string, file: File) => http.request<Out<"documents_upload_library">>("PUT", `/documents/library/${id}`, { body: file, query: { expected_version: version, title }, headers: { "X-Filename": encodeURIComponent(file.name) } }),
    restoreFileVersion: (id: string, version: number, expected: number) => post<Out<"documents_restore_library">>(`/documents/library/${id}/restore/${version}`, undefined, { query: { expected_version: expected } }),
    downloadFileVersion: (id: string, version: number) => file("documents_download_library", `/documents/library/${id}/versions/${version}/content`),
    listDocuments: (owner: DocumentOwnerFilter, page: PageRequest = {}) =>
      get<Out<"documents_list_documents">>(
        "/documents",
        paged(page, "householdId" in owner ? { household_id: owner.householdId } : { owner_type: owner.ownerType, owner_id: owner.ownerId }),
      ),
    /** The file, as the server serves it (pictures and PDFs inline, everything else as an attachment); `filename` from Content-Disposition. */
    downloadDocument: (id: string, signal?: AbortSignal) =>
      file("documents_download_document", `/documents/${id}/content`, signal),
  };
}
